import { useState, useEffect } from "react";
import { API, useAuth, authFetch } from "@/App";
import { Trophy, Medal, Star, Crown, Flame, TrendingUp, Target, Zap, Info, Gift, Award } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLE_COLORS = { AREA_MANAGER: "#8B1A1A", SALES_EXECUTIVE: "#3B82F6", PROMOTOR: "#10B981" };

const LeaderboardPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("monthly");
  const [region, setRegion] = useState(user?.region_id || "ALL");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ period });
        if (region !== "ALL") params.append("region", region);
        const res = await authFetch(`${API}/leaderboard?${params}`);
        const json = await res.json();
        if (json.success) setData(json.data || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [period, region]);

  const topThree = data.slice(0, 3);
  const rest = data.slice(3);

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Leaderboard</h1>
          <p className="text-sm text-slate-500 mt-1">Compete, perform, achieve — top scorers get recognition!</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[130px] h-9" data-testid="period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Today</SelectItem>
              <SelectItem value="weekly">This Week</SelectItem>
              <SelectItem value="monthly">This Month</SelectItem>
              <SelectItem value="all_time">All Time</SelectItem>
            </SelectContent>
          </Select>
          {user?.role === "SUPER_ADMIN" && (
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="w-[120px] h-9" data-testid="region-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Regions</SelectItem>
                <SelectItem value="GJ">Gujarat</SelectItem>
                <SelectItem value="RJ">Rajasthan</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#8B1A1A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data.length === 0 ? (
        <Card className="p-12 text-center text-slate-400">
          <Trophy className="w-14 h-14 mx-auto mb-3 text-slate-200" />
          <p className="text-sm">No performance data for this period</p>
        </Card>
      ) : (
        <>
          {/* Scoring criteria + Special benefits (P1 iter 80) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="leaderboard-callouts">
            <Card className="p-4 border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-900/40" data-testid="scoring-criteria">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">How the score is calculated</p>
                  <ul className="text-xs text-blue-700 dark:text-blue-300 mt-1 space-y-0.5 list-disc list-inside">
                    <li><span className="font-mono font-semibold">10 pts</span> per delivered order</li>
                    <li><span className="font-mono font-semibold">+1 pt</span> per ₹10,000 collected</li>
                    <li><span className="font-mono font-semibold">0.5 pt</span> per % of target achieved</li>
                    <li><span className="font-mono font-semibold">2 pts</span> per dealer visit</li>
                  </ul>
                </div>
              </div>
            </Card>
            <Card className="p-4 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 dark:border-amber-900/40" data-testid="special-benefits">
              <div className="flex items-start gap-3">
                <Gift className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-2">
                    <span>Special benefits for top performers</span>
                    <span className="inline-flex items-center rounded-md border bg-amber-200 text-amber-900 text-[10px] px-1.5 py-0.5 font-semibold">Premium</span>
                  </p>
                  <ul className="text-xs text-amber-800 dark:text-amber-300 mt-1 space-y-0.5">
                    <li className="flex items-center gap-1.5"><Crown className="w-3 h-3 text-amber-600" /> Rank #1 — Cash reward + Gold badge + recognition on monthly call</li>
                    <li className="flex items-center gap-1.5"><Award className="w-3 h-3 text-slate-500" /> Rank #2-3 — Silver/Bronze badge + ₹2,000 gift voucher</li>
                    <li className="flex items-center gap-1.5"><Star className="w-3 h-3 text-orange-500" /> 100%+ target achievers — Quarterly leadership offsite eligibility</li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
          {/* Champion Podium */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-b from-amber-50/50 to-transparent rounded-2xl" />
            <div className="grid grid-cols-3 gap-6 items-end py-6 px-4 relative" data-testid="top-three">
              {[1, 0, 2].map((idx) => {
                const emp = topThree[idx];
                if (!emp) return <div key={idx} />;
                const isChamp = emp.rank === 1;
                const heights = { 0: "h-48", 1: "h-56", 2: "h-44" };
                const podiumColors = { 1: "from-amber-400 to-amber-600", 2: "from-slate-300 to-slate-500", 3: "from-orange-300 to-orange-500" };
                const bgGlow = { 1: "shadow-amber-200/50", 2: "shadow-slate-200/50", 3: "shadow-orange-200/50" };
                const order = idx === 0 ? "order-2" : idx === 1 ? "order-1" : "order-3";
                
                return (
                  <div key={emp.employee_id} className={`${order} flex flex-col items-center`} 
                    style={{ animation: `slideUp 0.6s ease-out ${idx * 0.15}s both` }} data-testid={`podium-${emp.rank}`}>
                    {/* Avatar */}
                    <div className="relative mb-3">
                      {isChamp && (
                        <Crown className="w-8 h-8 text-amber-500 absolute -top-6 left-1/2 -translate-x-1/2" 
                          style={{ animation: "pulse 2s ease-in-out infinite" }} />
                      )}
                      <div className={`${isChamp ? "w-20 h-20" : "w-16 h-16"} rounded-full flex items-center justify-center text-white text-2xl font-black shadow-xl ${bgGlow[emp.rank]}`}
                        style={{ backgroundColor: ROLE_COLORS[emp.role] || "#6B7280", boxShadow: isChamp ? "0 0 30px rgba(245,166,35,0.4)" : undefined }}>
                        {emp.full_name?.charAt(0)}
                      </div>
                      <div className={`absolute -bottom-1 -right-1 ${isChamp ? "w-8 h-8" : "w-6 h-6"} rounded-full bg-gradient-to-br ${podiumColors[emp.rank]} flex items-center justify-center text-white text-xs font-black shadow-lg`}>
                        #{emp.rank}
                      </div>
                    </div>
                    
                    <h3 className={`font-bold text-slate-900 ${isChamp ? "text-base" : "text-sm"}`}>{emp.full_name}</h3>
                    <p className="text-[10px] text-slate-500 mt-0.5">{emp.role?.replace(/_/g, " ")}</p>
                    
                    {/* Score with fire effect */}
                    <div className="flex items-center gap-1 mt-2">
                      {isChamp && <Flame className="w-4 h-4 text-amber-500" />}
                      <span className={`font-black font-mono ${isChamp ? "text-3xl text-amber-600" : "text-2xl text-slate-800"}`}>{emp.score}</span>
                    </div>
                    <p className="text-[9px] text-slate-400 uppercase tracking-widest font-semibold">Points</p>
                    
                    {/* Stats bar */}
                    <div className="w-full grid grid-cols-2 gap-2 mt-3">
                      <div className="bg-white/80 backdrop-blur rounded-lg p-2 text-center border border-slate-100">
                        <p className="text-xs font-bold font-mono text-slate-900">{emp.delivered}</p>
                        <p className="text-[8px] text-slate-400">Delivered</p>
                      </div>
                      <div className="bg-white/80 backdrop-blur rounded-lg p-2 text-center border border-slate-100">
                        <p className="text-xs font-bold font-mono text-slate-900">{emp.target_pct}%</p>
                        <p className="text-[8px] text-slate-400">Target</p>
                      </div>
                    </div>

                    {/* Badge */}
                    {emp.badge && (
                      <div className={`mt-3 px-3 py-1 rounded-full text-[10px] font-bold border ${
                        emp.badge === "GOLD" ? "bg-amber-50 text-amber-700 border-amber-300" :
                        emp.badge === "SILVER" ? "bg-slate-50 text-slate-700 border-slate-300" :
                        "bg-orange-50 text-orange-700 border-orange-300"
                      }`} style={{ animation: "pulse 2s infinite" }}>
                        {emp.badge === "GOLD" ? <span>&#x1F451;</span> : emp.badge === "SILVER" ? <span>&#x1F948;</span> : <span>&#x1F949;</span>}
                        {" "}{emp.badge} PERFORMER
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rest Rankings */}
          {rest.length > 0 && (
            <Card className="overflow-hidden" data-testid="rankings-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider p-3 w-12">Rank</th>
                      <th className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider p-3">Employee</th>
                      <th className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider p-3">Region</th>
                      <th className="text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider p-3">Score</th>
                      <th className="text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider p-3">Orders</th>
                      <th className="text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider p-3">Delivered</th>
                      <th className="text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider p-3">Target %</th>
                      <th className="text-center text-[10px] font-semibold text-slate-500 uppercase tracking-wider p-3">Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rest.map((emp, i) => (
                      <tr key={emp.employee_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`rank-${emp.rank}`}
                        style={{ animation: `fadeIn 0.3s ease-out ${i * 0.05}s both` }}>
                        <td className="p-3">
                          <span className="inline-flex w-7 h-7 rounded-full bg-slate-100 items-center justify-center text-xs font-bold text-slate-500 font-mono">
                            {emp.rank}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                              style={{ backgroundColor: ROLE_COLORS[emp.role] || "#6B7280" }}>{emp.full_name?.charAt(0)}</div>
                            <div>
                              <p className="text-xs font-semibold text-slate-800">{emp.full_name}</p>
                              <p className="text-[10px] text-slate-400">{emp.role?.replace(/_/g, " ")}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3"><Badge variant="outline" className="text-[10px]">{emp.region_id}</Badge></td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Zap className="w-3 h-3 text-amber-500" />
                            <span className="font-mono font-bold text-sm text-slate-900">{emp.score}</span>
                          </div>
                        </td>
                        <td className="p-3 text-right font-mono text-sm text-slate-700">{emp.orders}</td>
                        <td className="p-3 text-right font-mono text-sm text-slate-700">{emp.delivered}</td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" 
                                style={{ width: `${Math.min(emp.target_pct, 100)}%`, backgroundColor: emp.target_pct >= 80 ? "#10B981" : emp.target_pct >= 50 ? "#F5A623" : "#EF4444" }} />
                            </div>
                            <span className="font-mono text-xs text-slate-700">{emp.target_pct}%</span>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          {emp.badge ? (
                            <Badge className={`text-[9px] ${
                              emp.badge === "GOLD" ? "bg-amber-50 text-amber-700 border-amber-300" :
                              emp.badge === "SILVER" ? "bg-slate-50 text-slate-700 border-slate-300" :
                              "bg-orange-50 text-orange-700 border-orange-300"
                            }`}>{emp.badge}</Badge>
                          ) : <span className="text-slate-300 text-xs">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default LeaderboardPage;
